import Component from '@bythepixel/component-loader';
import Tabs from '@Core/components/tabs';
import {
  enableBodyScroll,
  disableBodyScroll,
  fadeOut,
  fadeIn,
} from '@Core/utility';
import { Events } from './events';
import Config from './config';

export default class NavigationHeader extends Component {
  static selector = '[data-comp-navigationheader]';
  #navTabs;

  mobileNavOpen = false;
  activePanel = null;

  constructor(containerEl, options = Config) {
    super(containerEl, options);
    this.breakpoints = options.breakpoints;
    this.mobileNavTrigger = containerEl.querySelector(options.mobileNavTrigger);
    this.mobileContainer = containerEl.querySelector(options.mobileContainer);
    this.mobileMainPanel = containerEl.querySelector(options.mobileMainPanel);
    this.contentPanels = containerEl.querySelectorAll(
      options.mobilePanelContent,
    );
    this.mobilePanelTriggers = containerEl.querySelectorAll(
      options.mobilePanelTrigger,
    );
    this.overlay = document.querySelector(options.pageOverlay);
    this.mobileHeader = this.$container.querySelector(options.mobileHeader);
    this.banner = this.$container.querySelector(options.banner);

    this.mount();
  }

  position() {
    return this.$container.getBoundingClientRect();
  }

  isMobile() {
    return window.matchMedia(`(max-width: ${this.breakpoints.style}px)`)
      .matches;
  }

  isDesktop() {
    return !this.isMobile();
  }

  reset(rebuildTabs = false) {
    this.closeAllPanels();
    this.clearAnimationStyles();

    if (rebuildTabs) {
      this.buildTabs();
    }

    this.mobileNavOpen = false;
    this.$container.classList.remove(this.$options.activeClass);
    this.$container.classList.remove(this.$options.mobileNavActiveClass);
    enableBodyScroll();
    window.dispatchEvent(Events.Closed);
  }

  clearAnimationStyles() {
    this.contentPanels.forEach((panel) => {
      panel.setAttribute('style', null);
    });
  }

  attachListeners() {
    window
      .matchMedia(`(min-width: ${this.breakpoints.style}px)`)
      .addEventListener('change', () => {
        this.updateGlobalBottomCssVar();
        const rebuildTabs = !!this.isDesktop();
        this.reset(rebuildTabs);
      });

    window
      .matchMedia(`(min-width: ${this.breakpoints.small}px)`)
      .addEventListener('change', () => {
        this.updateGlobalBottomCssVar();
      });

    window
      .matchMedia(`(min-width: ${this.breakpoints.large}px)`)
      .addEventListener('change', () => {
        this.updateGlobalBottomCssVar();
      });

    this.mobilePanelTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panelId = trigger.dataset.mobilePanelTrigger;

        this.closeActivePanel();
        this.openPanel(panelId);
      });
    });

    this.mobileNavTrigger.addEventListener('click', () => {
      if (this.mobileNavOpen) {
        this.reset();
        return;
      }

      this.openMobileNav();
    });

    this.overlay.addEventListener('click', () => {
      if (!this.mobileNavOpen) return;

      this.reset();
    });
  }

  buildTabs() {
    const vehicleCategory = window.location.pathname
      .split('/')[1]
      .toLowerCase();
    const navTabComponent = this.$container.querySelector('[data-nav-tabs]') ?? null;

    if (!navTabComponent) return;

    const onSelect = (activeTab, inactiveTabs) => {
      const tabContainer = this.$container;

      inactiveTabs.forEach((tab) => {
        const navTabsId = tab.getAttribute('data-nav-tabs-id');
        const hideEl = tabContainer.querySelector(
          `[data-nav-tabs-content="${navTabsId}"]`,
        );
        hideEl.classList.remove('active');

        if (this.isMobile()) return;

        fadeOut(hideEl, {
          done() {
            const activeTabsId = activeTab.getAttribute('data-nav-tabs-id');
            const showEl = tabContainer.querySelector(
              `[data-nav-tabs-content="${activeTabsId}"]`,
            );
            fadeIn(showEl);
            showEl.classList.add('active');
          },
        });
      });
    };

    const tabOptions = {
      // For the Horse & Livestock nav, activate the Livestock Trailers
      // tab by default if within livestock trailers URLs
      initSelect: vehicleCategory === 'livestock-trailers' ? 1 : 0,

      childSelector: '[data-nav-tabs-id]',
      activeClass: 'active',
      onSelect,
    };

    this.#navTabs = new Tabs(navTabComponent, tabOptions);

    if (this.subnavSetupComplete) return;

    /**
     * IMPORTANT
     *
     * This timeout is necessary to ensure the user, if by some chance they're hovering
     * over a subnav item on page load, doesn't see all the subnav items flash in and out.
     */
    setTimeout(() => {
      this.subnavSetupComplete = true;
    }, 500);
  }

  offsetMobileContainer() {
    const offset = this.mobileHeader.offsetHeight;

    this.mobileContainer.style.top = `${offset}px`;
  }

  openMobileNav() {
    this.offsetMobileContainer();
    this.closeActivePanel();
    this.clearAnimationStyles();
    disableBodyScroll();

    this.$container.classList.add(this.$options.mobileNavActiveClass);
    this.openPanel(this.$options.mainMobilePanelId);

    this.mobileNavTrigger.setAttribute('aria-expanded', 'true');
    this.mobileNavOpen = true;
    window.dispatchEvent(Events.Open);
  }

  getPanel(panelId) {
    return this.$container.querySelector(
      `[data-mobile-panel-content="${panelId}"]`,
    );
  }

  openPanel(panelId) {
    this.activePanel = this.getPanel(panelId);
    this.activePanel.classList.add(this.$options.activeClass);
    this.activePanel.classList.remove(this.$options.hideClass);
  }

  closeActivePanel() {
    if (!this.activePanel) return;

    this.activePanel.classList.remove(this.$options.activeClass);
  }

  closeAllPanels() {
    this.contentPanels.forEach((panel) => {
      panel.classList.remove(this.$options.activeClass);
    });
  }

  setInitialDesktopTab() {
    const firstPanelId = this.mobilePanelTriggers[0]?.dataset.mobilePanelTrigger || null;
    if (!firstPanelId) return;
    this.openPanel(firstPanelId);
  }

  updateGlobalBottomCssVar() {
    let { bottom } = this.$container.getBoundingClientRect();
    const borderThickness = getComputedStyle(this.$container).borderBottomWidth.replace('px', '')
      || 0;

    bottom -= borderThickness;
    document.documentElement.style.setProperty(
      Config.bottomCssVarName,
      `${bottom}px`,
    );
  }

  get subnavSetupComplete() {
    return this.$container.dataset.subnavSetupComplete === 'true';
  }

  set subnavSetupComplete(updatedState) {
    this.$container.dataset.subnavSetupComplete = updatedState;
  }

  mount() {
    this.updateGlobalBottomCssVar();
    this.attachListeners();
    this.offsetMobileContainer();
    this.buildTabs();

    if (this.isDesktop()) {
      this.setInitialDesktopTab();
    }
  }
}
